import axios from "axios";
import {
  Edit,
  Eye,
  MoreVertical,
  Trash2,
  Package,
  Search,
  Filter,
  Download,
  X,
  Upload,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  LoaderIcon,
} from "lucide-react";
import { useEffect, useState } from "react";

import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
import {
  confirmAction,
  showError,
  showSuccess,
} from "../../../utils/toastUtils";
import AddProductDrawer from "../../../components/AdminSideComponents/products/AddProductDrawer";
import { useNavigate } from "react-router-dom";
import UploadProductsExcelDrawer from "../../../components/AdminSideComponents/products/UploadProductsExcelDrawer";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import { motion, AnimatePresence } from "framer-motion";

const ITEMS_PER_PAGE = 10;

const exportToPDF = async (products) => {
  if (!products.length) return;

  const { jsPDF } = await import("jspdf");
  const autoTable = (await import("jspdf-autotable")).default;

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const marginX = 8;
  const marginTop = 12;

  doc.setFontSize(16);
  doc.text("Products Report", 14, 15);

  autoTable(doc, {
    startY: marginTop + 6,

    margin: {
      left: marginX,
      right: marginX,
    },

    tableWidth: "auto",
    startY: 22,
    head: [
      [
        "Product",
        "Brand",
        "Vendor",
        "Usage",
        "MRP",
        "Qty",
        "Discount %",
        "Final Price",
      ],
    ],
    body: products.map((p) => {
      const base = p.mrp * p.quantity;
      const discount = (base * p.discount) / 100;
      const final = base - discount;

      return [
        (p.name || "").toUpperCase(),
        (p.brand || "").toUpperCase(),
        (p.vendorName || "").toUpperCase(),
        p.usageType === "use_and_throw" ? "Use & Throw" : "Reusable",
        `INR ${p.mrp}`,
        p.quantity,
        `${p.discount}%`,
        `INR ${final.toFixed(2)}`,
      ];
    }),
    styles: {
      fontSize: 8,
      cellPadding: 2,
      valign: "middle",
    },

    headStyles: {
      fillColor: [237, 181, 164],
      textColor: 0,
      fontSize: 9,
      cellPadding: 3,
    },

    columnStyles: {
      0: { cellWidth: 35 }, // Product
      3: { cellWidth: 30 }, // Vendor
      5: { cellWidth: 18 }, // MRP
      6: { cellWidth: 14 }, // Qty
      8: { cellWidth: 22 }, // Final Price
    },
  });

  doc.text(
    `Total Amount: INR ${calculateTotalAmount(products).toFixed(2)}`,
    14,
    doc.lastAutoTable.finalY + 10,
  );

  doc.save("Products_Report.pdf");
};

// TRANSFORM PRODUCT DATA
const transformProducts = (products = []) => {
  if (!Array.isArray(products)) return [];

  return products.map((p) => ({
    id: p._id,
    raw: p,
    name: p.productName,
    brand: p.brand,
    mrp: p.mrp,
    quantity: p.quantity,
    discount: p.discountPercent,
    usageType: p.productUsageType,
    vendorName: p.vendor?.vendorName || "-",
    vendorId: p.vendor?._id || null,
  }));
};

const calculateTotalAmount = (list = []) => {
  return list.reduce((sum, p) => {
    const base = p.mrp * p.quantity;
    const discount = (base * p.discount) / 100;
    return sum + (base - discount);
  }, 0);
};

// EXPORT TO EXCEL
const exportToExcel = async (products) => {
  try {
    showSuccess("Preparing Excel export...");

    // Import required library
    const XLSX = await import("xlsx");

    // Prepare data for export
    const exportData = products.map((p, index) => {
      const basePrice = p.mrp * p.quantity;
      const discountAmount = (basePrice * p.discount) / 100;
      const finalPrice = basePrice - discountAmount;
      const unitPrice = p.mrp - (p.mrp * p.discount) / 100;

      return {
        "Sl No": index + 1,
        "Product Name": p.name,
        Brand: p.brand,
        Vendor: p.vendorName,
        "Usage Type":
          p.usageType === "use_and_throw"
            ? "Use_And_Throw"
            : p.usageType === "reusable"
              ? "Reusable"
              : "-",
        MRP: p.mrp,
        Quantity: p.quantity,
        "Base Price": basePrice,
        "Discount %": p.discount,
        "Discount Amount": discountAmount,
        "Final Price": finalPrice,
        "Unit Price (After Discount)": unitPrice,
      };
    });

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Set column widths
    const columnWidths = [
      { wch: 8 }, // Sl No
      { wch: 25 }, // Product Name
      { wch: 15 }, // Brand
      { wch: 20 }, // Vendor
      { wch: 18 }, // Service
      { wch: 15 }, // Usage Type
      { wch: 12 }, // MRP
      { wch: 10 }, // Quantity
      { wch: 14 }, // Base Price
      { wch: 12 }, // Discount %
      { wch: 16 }, // Discount Amount
      { wch: 14 }, // Final Price
      { wch: 22 }, // Unit Price
    ];
    worksheet["!cols"] = columnWidths;

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Products");

    // Add summary sheet
    const summaryData = [
      { Metric: "Total Products", Value: products.length },
      {
        Metric: "Total Stock Quantity",
        Value: products.reduce((sum, p) => sum + p.quantity, 0),
      },
      { Metric: "Exported On", Value: new Date().toLocaleString() },
    ];
    const summarySheet = XLSX.utils.json_to_sheet(summaryData);
    summarySheet["!cols"] = [{ wch: 25 }, { wch: 30 }];
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `Products_Export_${timestamp}.xlsx`;

    // Save file
    XLSX.writeFile(workbook, filename);

    showSuccess(`Exported ${products.length} products successfully!`);
  } catch (error) {
    console.error("Export failed:", error);
    showError("Failed to export data. Please try again.");
  }
};

const AllProducts = () => {
  const [products, setProducts] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { admin } = useAdminAuth();

  const [currentPage, setCurrentPage] = useState(1);
  const [actionOpen, setActionOpen] = useState(null);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);

  const [showExcelUpload, setShowExcelUpload] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedUsageType, setSelectedUsageType] = useState("all");
  const [showUsageFilter, setShowUsageFilter] = useState(false);

  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState("all");
  const [showStockModal, setShowStockModal] = useState(false);
  const [stockChanges, setStockChanges] = useState({});
  const [bulkLoading, setBulkLoading] = useState(false);

  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );
  const totalAmount = calculateTotalAmount(filteredProducts);

  const isAllSelected =
    paginatedProducts.length > 0 &&
    paginatedProducts.every((p) => selectedProductIds.includes(p.id));

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedProductIds((prev) =>
        prev.filter((id) => !paginatedProducts.some((p) => p.id === id)),
      );
    } else {
      setSelectedProductIds((prev) => [
        ...new Set([...prev, ...paginatedProducts.map((p) => p.id)]),
      ]);
    }
  };

  const toggleSelectProduct = (id) => {
    setSelectedProductIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const selectedProducts = products.filter((p) =>
    selectedProductIds.includes(p.id),
  );

  // FETCH PRODUCTS - FIXED
  const fetchProducts = async (usageType = "all") => {
    setIsLoading(true);
    try {
      let url = `${process.env.REACT_APP_API_URL}/products/get-all`;

      if (usageType !== "all") {
        url += `?productUsageType=${usageType}`;
      }

      const res = await axios.get(url);

      const transformed = transformProducts(res?.data?.data || []);
      setProducts(transformed);
      setFilteredProducts(transformed);
    } catch (err) {
      console.error("Fetch products failed", err);
      setProducts([]);
      setFilteredProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  // console.log(admin)

  const handleBulkStockUpdate = async () => {
    try {
      setBulkLoading(true);
      const updates = Object.entries(stockChanges)
        .filter(([_, qty]) => qty !== 0)
        .map(([productId, qty]) => ({
          productId,
          quantityChange: qty,
        }));

      await axios.patch(
        `${process.env.REACT_APP_API_URL}/products/bulk-stock-update`,
        {
          updates,
          performedBy: admin.id,
        },
      );

      showSuccess("Stock updated successfully");
      setShowStockModal(false);
      setSelectedProductIds([]);
      fetchProducts(selectedUsageType);
    } catch (err) {
      showError(err.response?.data?.message || "Bulk update failed");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedProductIds.length) return;

    const confirmed = await confirmAction({
      title: "Delete Selected Products?",
      text: `This will permanently delete ${selectedProductIds.length} product(s).`,
      confirmText: "Yes, Delete",
      cancelText: "Cancel",
      icon: "warning",
    });

    if (!confirmed) return;

    try {
      setIsLoading(true);
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/products/bulk-delete`,
        {
          data: { productIds: selectedProductIds, performedBy: admin.id },
        },
      );

      showSuccess("Selected products deleted successfully");

      setSelectedProductIds([]);
      fetchProducts(selectedUsageType);
    } catch (err) {
      showError(err?.response?.data?.message || "Bulk delete failed");
    } finally {
      setIsLoading(false);
    }
  };

  // console.log(categories)

  useEffect(() => {
    fetchProducts(selectedUsageType);
  }, [selectedUsageType]);

  useEffect(() => {
    setSelectedProductIds([]);
  }, [searchQuery, selectedCategory, selectedVendor, selectedUsageType]);

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/vendors`);
        setVendors(res.data.data || []);
      } catch {
        showError("Failed to load vendors");
      }
    };

    fetchVendors();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchQuery, selectedCategory, selectedVendor, products]);

  const applyFilters = () => {
    let filtered = [...products];

   
    if (selectedVendor !== "all") {
      filtered = filtered.filter((p) => p.vendorId === selectedVendor);
    }

    if (searchQuery.trim()) {
      const lower = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name?.toLowerCase().includes(lower) ||
          p.brand?.toLowerCase().includes(lower) ||
          p.vendorName?.toLowerCase().includes(lower),
      );
    }

    setFilteredProducts(filtered);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  
  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("all");
    setSelectedUsageType("all");
    setSelectedVendor("all");
  };

  // DELETE PRODUCT
  const handleDeleteProduct = async (product) => {
    const confirmed = await confirmAction({
      title: "Delete Product?",
      text: `This will permanently delete ${product.name}.`,
      confirmText: "Yes, Delete",
      cancelText: "Cancel",
      icon: "warning",
    });

    if (!confirmed) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/products/${product.id}`,
      );
      showSuccess("Product deleted successfully");
      setActionOpen(null);
      fetchProducts(selectedUsageType);
    } catch (err) {
      showError(err?.response?.data?.message || "Delete failed");
    }
  };

  // Get active filters count
  const activeFiltersCount =
    (selectedCategory !== "all" ? 1 : 0) +
    (selectedUsageType !== "all" ? 1 : 0) +
    (selectedVendor !== "all" ? 1 : 0) +
    (searchQuery.trim() ? 1 : 0);

  const handleNavigate = (url) => {
    navigate(url);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="flex flex-col px-6 min-w-0 w-full bg-gradient-to-br min-h-screen from-[#FFF7F3] to-gray-50">
      <div className=" py-2 flex items-center justify-between">
        <Breadcrumb title1="Products" title2="Products Management" />
        {/* Action Buttons Row - Responsive */}
        <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
          {/* Export Excel Button */}
          <button
            onClick={() => exportToExcel(filteredProducts)}
            disabled={filteredProducts.length === 0}
            className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 sm:py-2.5 text-sm bg-green-500 text-white font-medium rounded-lg hover:bg-green-600 transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
          >
            <Download size={16} className="sm:w-[18px] sm:h-[18px]" />
            <span className="hidden sm:inline">Export Excel</span>
            <span className="sm:hidden">Excel</span>
          </button>

          {/* Export PDF Button */}
          <button
            onClick={() => exportToPDF(filteredProducts)}
            disabled={!filteredProducts.length}
            className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 sm:py-2.5 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center justify-center gap-2 disabled:opacity-50 active:scale-95 font-medium"
          >
            <Download size={16} className="sm:w-[18px] sm:h-[18px]" />
            <span className="hidden sm:inline">Export PDF</span>
            <span className="sm:hidden">PDF</span>
          </button>

          {/* Upload Excel Button */}
          <button
            onClick={() => setShowExcelUpload(true)}
            className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 sm:py-2.5 text-sm bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition flex items-center justify-center gap-2 active:scale-95"
          >
            <Upload size={16} className="sm:w-[18px] sm:h-[18px]" />
            <span className="hidden sm:inline">Upload Excel</span>
            <span className="sm:hidden">Upload</span>
          </button>

          {/* Add Product Button */}
          <button
            onClick={() => setIsDrawerOpen(true)}
            className="flex-1 sm:flex-initial px-3 sm:px-6 py-2 sm:py-2.5 text-sm bg-primaryBtnColor text-white font-medium rounded-lg hover:bg-primaryBtnHoverColor transition flex items-center justify-center gap-2 active:scale-95"
          >
            <Package size={16} className="sm:w-[18px] sm:h-[18px]" />
            <span className="hidden sm:inline">Add Product</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
      </div>

      <div className="mb-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {/* Total Products */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-1.5 sm:p-3 shadow-sm hover:shadow-md transition flex items-center justify-between">
            <div>
              <div className="w-10 h-10 border sm:w-12 sm:h-12 bg-gradient-to-br from-blue-700 via-blue-300 to-blue-700 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <p className="text-xs sm:text-sm text-blue-700 font-semibold mt-2">
                Total Products
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl sm:text-3xl font-bold text-blue-900">
                {filteredProducts.length}
              </p>
              <p className="text-xs text-blue-600 mt-2 font-semibold capitalize">
                of {products.length} total
              </p>
            </div>
          </div>

          {/* Total Value */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-1.5 sm:p-3 shadow-sm hover:shadow-md transition flex items-center justify-between">
            <div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-700 via-green-300 to-green-700 rounded-lg flex items-center justify-center">
                <Download className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <p className="text-xs sm:text-sm text-green-700 font-semibold mt-2">
                Total Value
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl sm:text-3xl font-bold text-green-900">
                ₹{totalAmount.toFixed(1)}
              </p>
              <p className="text-xs text-green-600 mt-2 font-semibold capitalize">
                Based on filters
              </p>
            </div>
          </div>

          {/* Total Quantity */}
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-1.5 sm:p-3 shadow-sm hover:shadow-md transition flex items-center justify-between">
            <div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-700 via-purple-300 to-purple-700 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <p className="text-xs sm:text-sm text-purple-700 font-semibold mt-2">
                Total Stock
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl sm:text-3xl font-bold text-purple-900">
                {filteredProducts
                  .reduce((sum, p) => sum + p.quantity, 0)
                  .toLocaleString("en-IN")}
              </p>
              <p className="text-xs text-purple-600 mt-2 font-semibold capitalize">
                Items in inventory
              </p>
            </div>
          </div>
         
        </div>
      </div>

      <div className="mb-6 bg-white rounded-md shadow-lg p-3 sm:p-6">
        {/* FILTERS AND ACTIONS BAR - Responsive Layout */}
        <div className="flex flex-col gap-3 sm:gap-4">
          {/* Search Bar - Full Width on Mobile */}
          <div className="w-full flex justify-between flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-4 sm:w-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search products..."
                className="w-full shadow-md pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-accentTextColor transition"
              />
            </div>
            {/* Filters Row - Wrap on smaller screens */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">

              {/* Usage Type Filter */}
              <div className="relative">
                <button
                  onClick={() => setShowUsageFilter(!showUsageFilter)}
                  className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm border shadow-lg rounded-lg font-medium flex items-center gap-2 transition ${selectedUsageType !== "all"
                      ? "bg-green-50 border-green-300 text-green-700"
                      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                >
                  <Filter size={16} className="sm:w-[18px] sm:h-[18px]" />
                  <span className="hidden sm:inline">Usage Type</span>
                  <span className="sm:hidden">Usage</span>
                  {selectedUsageType !== "all" && (
                    <span className="px-1.5 sm:px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">
                      1
                    </span>
                  )}
                </button>

                {showUsageFilter && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowUsageFilter(false)}
                    />
                    <div className="absolute top-full mt-2 left-0 w-48 sm:w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-20">
                      <div className="p-2 space-y-1">
                        {[
                          { label: "All", value: "all" },
                          { label: "Use_And_Throw", value: "use_and_throw" },
                          { label: "Reusable", value: "reusable" },
                        ].map((item) => (
                          <button
                            key={item.value}
                            onClick={() => {
                              setSelectedUsageType(item.value);
                              setShowUsageFilter(false);
                            }}
                            className={`w-full text-left px-3 sm:px-4 py-2 text-sm rounded-lg transition ${selectedUsageType === item.value
                                ? "bg-green-50 text-green-700 font-medium"
                                : "hover:bg-gray-100"
                              }`}
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Vendor Filter */}
              <div className="relative flex-1 min-w-[120px] sm:min-w-0 sm:flex-initial">
                <select
                  value={selectedVendor}
                  onChange={(e) => setSelectedVendor(e.target.value)}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border shadow-lg rounded-lg text-gray-700 bg-white font-medium hover:bg-gray-50 transition focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  <option value="all">All Vendors</option>
                  {vendors.map((v) => (
                    <option key={v._id} value={v._id}>
                      {v.vendorName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Clear Filters */}
              {activeFiltersCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 font-medium flex items-center gap-2 transition border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
                >
                  <X size={16} />
                  <span className="hidden sm:inline">Clear</span>
                </button>
              )}
            </div>
            {selectedProductIds.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowStockModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium"
                >
                  Manage Stock ({selectedProductIds.length})
                </button>

                <button
                  onClick={handleBulkDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium"
                >
                  Delete Selected
                </button>
              </div>
            )}
          </div>

          {/* Results Summary - Responsive */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs sm:text-sm text-gray-600">
            <div className="flex items-center gap-2 flex-wrap">
              {selectedCategory !== "all" && (
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                  {selectedCategory}
                </span>
              )}
              {selectedUsageType !== "all" && (
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                  {selectedUsageType === "use_and_throw"
                    ? "Use_And_Throw"
                    : "Reusable"}
                </span>
              )}
              {selectedVendor !== "all" && (
                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium truncate max-w-[150px]">
                  {vendors.find((v) => v._id === selectedVendor)?.vendorName}
                </span>
              )}
              {activeFiltersCount > 0 && (
                <div className="text-blue-600 font-medium text-xs sm:text-sm">
                  {activeFiltersCount} filter{activeFiltersCount > 1 ? "s" : ""}{" "}
                  active
                </div>
              )}
            </div>
          </div>
        </div>

        {/* TABLE - Horizontal Scroll for All Screens */}
        <div className="overflow-x-auto no-scrollbar -mx-3 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden">
              <table className="min-w-full border border-gray-300">
                <thead>
                  <tr className="text-left bg-[#EDB5A4] text-primaryTextColor text-[10px] sm:text-xs uppercase tracking-wider">
                    <th className="p-2 text-center">
                      <input
                        type="checkbox"
                        checked={
                          filteredProducts.length > 0 &&
                          selectedProductIds.length === filteredProducts.length
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedProductIds(
                              filteredProducts.map((p) => p.id),
                            );
                          } else {
                            setSelectedProductIds([]);
                          }
                        }}
                      />
                    </th>
                    <th className="p-2 text-center whitespace-nowrap">Sl No</th>
                    <th className="p-2 sm:p-3 whitespace-nowrap min-w-[150px]">
                      Product Name
                    </th>
                    <th className="p-2 sm:p-3 whitespace-nowrap min-w-[100px]">
                      Brand
                    </th>
                    <th className="p-2 sm:p-3 whitespace-nowrap min-w-[120px]">
                      Vendor
                    </th>
                    <th className="p-2 sm:p-3 whitespace-nowrap min-w-[100px]">
                      Usage Type
                    </th>
                    <th className="p-2 sm:p-3 text-center whitespace-nowrap">
                      MRP
                    </th>
                    <th className="p-2 sm:p-3 text-center whitespace-nowrap">
                      Quantity
                    </th>
                    <th className="p-2 sm:p-3 text-center whitespace-nowrap">
                      Discount
                    </th>
                    <th className="p-2 sm:p-3 text-right whitespace-nowrap">
                      Final Price
                    </th>
                    <th className="p-2 sm:p-3 text-right whitespace-nowrap">
                      Unit Price
                    </th>
                    <th className="p-2 sm:p-3 text-center whitespace-nowrap">
                      Actions
                    </th>
                  </tr>
                </thead>

                {isLoading ? (
                  <ProductTableSkeleton rows={ITEMS_PER_PAGE} />
                ) : filteredProducts.length === 0 ? (
                  <EmptyState
                    hasFilters={activeFiltersCount > 0}
                    onClearFilters={clearFilters}
                  />
                ) : (
                  <tbody className="divide-y divide-gray-200 text-xs sm:text-sm">
                    {paginatedProducts.map((p, index) => {
                      const basePrice = p.mrp * p.quantity;
                      const discountAmount = (basePrice * p.discount) / 100;
                      const finalPrice = basePrice - discountAmount;
                      const unitPrice = p.mrp - (p.mrp * p.discount) / 100;

                      return (
                        <tr key={p.id} className="hover:bg-gray-50 transition">
                          <td className="p-2 text-center">
                            <input
                              type="checkbox"
                              checked={selectedProductIds.includes(p.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedProductIds((prev) => [
                                    ...prev,
                                    p.id,
                                  ]);
                                } else {
                                  setSelectedProductIds((prev) =>
                                    prev.filter((id) => id !== p.id),
                                  );
                                }
                              }}
                            />
                          </td>
                          <td className="p-2 sm:p-3 text-center">
                            {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                          </td>

                          <td
                            title="view product details"
                            onClick={() => {
                              handleNavigate(`/products-management/${p.id}`);
                              setActionOpen(null);
                            }}
                            className="p-2 sm:p-4 font-medium cursor-pointer capitalize hover:text-blue-600"
                          >
                            {p.name}
                          </td>

                          <td className="p-2 sm:p-4 capitalize">{p.brand}</td>
                          <td className="p-2 sm:p-4 capitalize">
                            {p.vendorName}
                          </td>
                          <td className="p-2 sm:p-4">
                            <span
                              className={`px-2 py-1 rounded text-[10px] sm:text-xs font-medium whitespace-nowrap ${p.usageType === "use_and_throw"
                                  ? "bg-orange-100 text-orange-700"
                                  : "bg-green-100 text-green-700"
                                }`}
                            >
                              {p.usageType === "use_and_throw"
                                ? "Use & Throw"
                                : "Reusable"}
                            </span>
                          </td>
                          <td className="p-2 sm:p-4 text-right whitespace-nowrap">
                            ₹{p.mrp.toFixed(2)}
                          </td>
                          <td className="p-2 sm:p-4 text-center">
                            {p.quantity}
                          </td>
                          <td className="p-2 sm:p-4 text-center">
                            {p.discount > 0 ? (
                              <span className="inline-flex items-center justify-center h-5 sm:h-6 min-w-[40px] sm:min-w-[48px] px-2 text-[10px] sm:text-xs font-medium rounded-full bg-green-50 text-green-700 border border-green-300">
                                {p.discount}%
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="p-2 sm:p-4 text-right font-semibold whitespace-nowrap">
                            ₹{finalPrice.toFixed(2)}
                          </td>
                          <td className="p-2 sm:p-4 text-right font-semibold whitespace-nowrap">
                            ₹{unitPrice.toFixed(2)}
                          </td>

                          <td className="p-2 sm:p-4 text-center relative">
                            <button
                              onClick={() =>
                                setActionOpen((prev) =>
                                  prev === p.id ? null : p.id,
                                )
                              }
                              className="p-1.5 sm:p-2 hover:bg-gray-200 rounded-lg transition"
                            >
                              <MoreVertical
                                size={18}
                                className="sm:w-5 sm:h-5 text-gray-600"
                              />
                            </button>

                            {actionOpen === p.id && (
                              <>
                                <div
                                  className="fixed inset-0 z-10"
                                  onClick={() => setActionOpen(null)}
                                />

                                <div
                                  className={`absolute right-8 sm:right-14 w-36 sm:w-40 bg-white rounded-lg shadow-xl border border-gray-200 z-50 ${index >= paginatedProducts?.length - 2
                                      ? "bottom-[5%]"
                                      : "top-10 sm:top-10"
                                    }`}
                                >
                                  <button
                                    onClick={() => {
                                      handleNavigate(
                                        `/products-management/${p.id}`,
                                      );
                                      setActionOpen(null);
                                    }}
                                    className="w-full text-left px-3 py-2 text-xs sm:text-sm hover:bg-gray-100 flex items-center gap-2 sm:gap-3"
                                  >
                                    <Eye size={14} className="sm:w-4 sm:h-4" />
                                    View Product
                                  </button>

                                  <button
                                    onClick={() => {
                                      setEditProduct(p.raw);
                                      setIsDrawerOpen(true);
                                      setActionOpen(null);
                                    }}
                                    className="w-full text-left px-3 py-2 text-xs sm:text-sm hover:bg-gray-100 flex items-center gap-2 sm:gap-3"
                                  >
                                    <Edit size={14} className="sm:w-4 sm:h-4" />
                                    Edit
                                  </button>

                                  <button
                                    onClick={() => handleDeleteProduct(p)}
                                    className="w-full text-left px-3 py-2 text-xs sm:text-sm hover:bg-red-50 text-red-600 flex items-center gap-2 sm:gap-3"
                                  >
                                    <Trash2
                                      size={14}
                                      className="sm:w-4 sm:h-4"
                                    />
                                    Delete
                                  </button>
                                </div>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                )}
              </table>
            </div>
          </div>
        </div>

        {/* PAGINATION - Responsive */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-0 mt-4 sm:mt-6">
            <div>
              <span className="font-medium text-gray-900 text-sm">
                Showing <span>{filteredProducts.length}</span> of{" "}
                <span className="font-medium text-gray-900">
                  {products.length}
                </span>{" "}
                products
              </span>
              <p className="text-xs sm:text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </p>
            </div>

            <div className="flex gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="px-3 sm:px-4 py-1.5 sm:py-2 text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition"
              >
                Prev
              </button>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="px-3 sm:px-4 py-1.5 sm:py-2 text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* MODALS */}
      <AddProductDrawer
        isOpen={isDrawerOpen}
        editingProduct={editProduct}
        onClose={() => {
          setIsDrawerOpen(false);
          setEditProduct(null);
        }}
        onAddProduct={() => fetchProducts(selectedUsageType)}
      />

      {showExcelUpload && (
        <UploadProductsExcelDrawer
          isOpen={showExcelUpload}
          onClose={() => setShowExcelUpload(false)}
          onSuccess={() => fetchProducts(selectedUsageType)}
        />
      )}

      {/* STOCK MANAGEMENT MODAL */}
      {showStockModal && (
        <AnimatePresence>
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowStockModal(false)}
              className="fixed inset-0 bg-black/50 z-50"
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="fixed inset-0 flex items-center justify-center z-50 p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-primaryColor via-brandAccentColor to-primaryColor">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                      <Package size={20} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">
                        Manage Stock
                      </h2>
                      <p className="text-sm text-white/80">
                        {selectedProducts.length} product
                        {selectedProducts.length !== 1 ? "s" : ""} selected
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowStockModal(false)}
                    className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition active:scale-95"
                  >
                    <X size={18} className="text-white" />
                  </button>
                </div>

                {/* Info Banner */}
                <div className="px-6 py-3 bg-blue-50 border-b border-blue-100">
                  <div className="flex items-start gap-2">
                    <AlertCircle
                      size={16}
                      className="text-blue-600 mt-0.5 flex-shrink-0"
                    />
                    <p className="text-sm text-blue-700">
                      Adjust stock quantities for selected products. Changes
                      will be applied immediately.
                    </p>
                  </div>
                </div>

                {/* Products List */}
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="space-y-4">
                    {selectedProducts.map((p, index) => {
                      const change = stockChanges[p.id] || 0;
                      const newStock = p.quantity + change;
                      const isIncreasing = change > 0;
                      const isDecreasing = change < 0;

                      return (
                        <motion.div
                          key={p.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition bg-gradient-to-br from-white to-gray-50"
                        >
                          <div className="flex items-center justify-between gap-4">
                            {/* Product Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <Package
                                    size={18}
                                    className="text-blue-600"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-semibold text-gray-900 truncate capitalize">
                                    {p.name}
                                  </h4>
                                  <p className="text-xs text-gray-500 capitalize">
                                    {p.brand || "No brand"}
                                  </p>
                                </div>
                              </div>

                              {/* Current Stock Info */}
                              <div className="flex items-center gap-4 text-xs">
                                <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full">
                                  <span className="text-gray-600">
                                    Current:
                                  </span>
                                  <span className="font-semibold text-gray-900">
                                    {p.quantity}
                                  </span>
                                </div>
                                {change !== 0 && (
                                  <>
                                    <span className="text-gray-400">→</span>
                                    <div
                                      className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${isIncreasing
                                          ? "bg-green-100 text-green-700"
                                          : "bg-red-100 text-red-700"
                                        }`}
                                    >
                                      {isIncreasing ? (
                                        <TrendingUp size={12} />
                                      ) : (
                                        <TrendingDown size={12} />
                                      )}
                                      <span className="font-semibold">
                                        {newStock}
                                      </span>
                                      <span className="text-xs">
                                        ({change > 0 ? "+" : ""}
                                        {change})
                                      </span>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Stock Controls */}
                            <div className="flex items-center gap-3">
                              {/* Decrease Button */}
                              <button
                                disabled={newStock <= 0}
                                onClick={() =>
                                  setStockChanges((prev) => ({
                                    ...prev,
                                    [p.id]: (prev[p.id] || 0) - 1,
                                  }))
                                }
                                className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-100 to-red-200 text-red-700 font-bold text-xl hover:from-red-200 hover:to-red-300 disabled:opacity-40 disabled:cursor-not-allowed transition active:scale-95 shadow-sm"
                              >
                                −
                              </button>

                              {/* Stock Display */}
                              <div className="min-w-[100px] text-center">
                                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
                                  <p className="text-xs text-blue-600 font-medium mb-1">
                                    New Stock
                                  </p>
                                  <p
                                    className={`text-2xl font-bold ${isIncreasing
                                        ? "text-green-600"
                                        : isDecreasing
                                          ? "text-red-600"
                                          : "text-gray-900"
                                      }`}
                                  >
                                    {newStock}
                                  </p>
                                  {change !== 0 && (
                                    <p
                                      className={`text-xs font-medium mt-1 ${isIncreasing
                                          ? "text-green-600"
                                          : "text-red-600"
                                        }`}
                                    >
                                      {change > 0 ? "+" : ""}
                                      {change} units
                                    </p>
                                  )}
                                </div>
                              </div>

                              {/* Increase Button */}
                              <button
                                onClick={() =>
                                  setStockChanges((prev) => ({
                                    ...prev,
                                    [p.id]: (prev[p.id] || 0) + 1,
                                  }))
                                }
                                className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-100 to-green-200 text-green-700 font-bold text-xl hover:from-green-200 hover:to-green-300 transition active:scale-95 shadow-sm"
                              >
                                +
                              </button>
                            </div>
                          </div>

                          {/* Warning for low stock */}
                          {newStock === 0 && (
                            <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                              <AlertCircle
                                size={14}
                                className="text-yellow-600"
                              />
                              <p className="text-xs text-yellow-700 font-medium">
                                Warning: Stock will be set to zero
                              </p>
                            </div>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
                  <div className="text-sm text-gray-600">
                    {Object.keys(stockChanges).filter(
                      (key) => stockChanges[key] !== 0,
                    ).length > 0 ? (
                      <span className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                        {
                          Object.keys(stockChanges).filter(
                            (key) => stockChanges[key] !== 0,
                          ).length
                        }{" "}
                        product(s) modified
                      </span>
                    ) : (
                      <span className="text-gray-400">No changes made</span>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setShowStockModal(false)}
                      className="px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition active:scale-95"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleBulkStockUpdate}
                      disabled={
                        Object.keys(stockChanges).filter(
                          (key) => stockChanges[key] !== 0,
                        ).length === 0
                      }
                      className="px-6 py-2.5 bg-primaryBtnColor text-white font-medium rounded-lg hover:bg-primaryBtnHoverColor transition active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {!bulkLoading ? (
                        <span className="flex items-center gap-2">
                          <Package size={18} />
                          Update Stock
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <LoaderIcon size={18} className="animate-spin" />
                          Updating..
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        </AnimatePresence>
      )}
    </div>
  );
};

// SKELETON LOADER
const ProductTableSkeleton = ({ rows = 10 }) => (
  <tbody>
    {Array.from({ length: rows }).map((_, i) => (
      <tr key={i} className="animate-pulse">
        {Array.from({ length: 13 }).map((_, j) => (
          <td key={j} className="p-2 sm:p-3">
            <div className="h-6 sm:h-8 bg-gray-200 rounded" />
          </td>
        ))}
      </tr>
    ))}
  </tbody>
);

// EMPTY STATE
const EmptyState = ({ hasFilters, onClearFilters }) => (
  <tbody>
    <tr>
      <td colSpan={12} className="py-12 sm:py-16 text-center">
        <div className="flex flex-col items-center gap-3 sm:gap-4 text-gray-500">
          <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gray-100 flex items-center justify-center">
            <Package className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
          </div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-700">
            {hasFilters
              ? "No products match your filters"
              : "No products found"}
          </h3>
          {hasFilters && (
            <button
              onClick={onClearFilters}
              className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
            >
              Clear Filters
            </button>
          )}
        </div>
      </td>
    </tr>
  </tbody>
);

export default AllProducts;
